'use client'

import { useEffect, useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { useIsMounted } from 'usehooks-ts'

import { EMPTY_USER_IMAGE_NAME, useAsset } from '@marketplace-web/shared/assets'

import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { useDebounce } from '@marketplace-web/shared/ui-helpers'
import { UserSelect } from '@marketplace-web/common-components/user-select-ui'
import {
  renderValidation,
  useFormValidationMessage,
} from '@marketplace-web/vendor-abstractions/react-hook-form-feature'
import { getHelpCenterRecentUsers } from '@marketplace-web/help-center-support-tickets/help-center-support-tickets-data'
import {
  transformUserSuggestions,
  UserSuggestionModel,
} from '@marketplace-web/search/search-bar-data'
import { useBreakpoint } from '@marketplace-web/breakpoints/breakpoints-feature'

import { Field } from './types'

const DEBOUNCE_DELAY = 350

type Props = {
  isMemberRequired?: boolean
}

const MemberSelect = ({ isMemberRequired }: Props) => {
  const translate = useTranslate('help_contact_form.member')
  const userSelectTranslate = useTranslate()
  const isMounted = useIsMounted()
  const breakpoints = useBreakpoint()
  const noPhotoAsset = useAsset('assets/no-photo')

  const [recentUsers, setRecentUsers] = useState<Array<UserSuggestionModel>>([])
  const [query, setQuery] = useState('')
  const [selectedValue, setSelectedValue] = useState<UserSuggestionModel | null>(null)

  const {
    setValue,
    control,
    clearErrors,
    formState: { errors },
  } = useFormContext()

  const validationMessage = useFormValidationMessage(errors, 'help_contact_form')

  const debouncedFunction = useDebounce((value: string) => {
    if (!isMounted()) return

    setQuery(value)
  }, DEBOUNCE_DELAY)

  const handleMemberSearch = ({ target }) => {
    debouncedFunction(target.value)
  }

  useEffect(() => {
    async function fetchRecentUsers() {
      const response = await getHelpCenterRecentUsers(query)

      if ('errors' in response) return

      setRecentUsers(transformUserSuggestions(response.users))
    }

    fetchRecentUsers()
  }, [query])

  function handleSave(user: UserSuggestionModel) {
    if (!user) return

    clearErrors(Field.RelatedMember)
    setSelectedValue(user)
    setValue(Field.RelatedMember, user.id)
  }

  return (
    <Controller
      defaultValue={null}
      name={Field.RelatedMember}
      control={control}
      rules={{ required: isMemberRequired }}
      render={() => (
        <UserSelect
          isPhone={!!breakpoints.phones}
          name={Field.RelatedMember}
          title={translate('title')}
          textSave={userSelectTranslate('actions.save')}
          placeholder={translate('placeholder')}
          users={recentUsers}
          onChange={handleMemberSearch}
          selectedUser={selectedValue}
          onSave={handleSave}
          validation={renderValidation(
            validationMessage(Field.RelatedMember, { required: translate('error') }),
          )}
          emptyUserImage={noPhotoAsset(EMPTY_USER_IMAGE_NAME)}
        />
      )}
    />
  )
}

export default MemberSelect
